import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import { toast } from "react-toastify";
import shiftManagementService from "services/shiftManagementService";
import yardService from "services/yardService";
import { SAVE_BUTTON_SX } from "shifts/shiftStyles";
import { useShiftPermissions } from "shifts/permissions";
import { ShiftHubOption } from "shifts/models";
import { YardReviewRow } from "yard/models";

// Yard Review — pending and approved yard shifts. Replaces the legacy Logistrics YardReview
// page, and restores in-list approval: the final legacy build hid the Approve column and made
// you go through the GPS route-details page to approve anything.
//
// Minutes / Miles / Trips are produced by GpsDataProcessorShift and are read-only here.

const YardReview = () => {
  const navigate = useNavigate();
  const { canApproveShift } = useShiftPermissions();
  const [hubs, setHubs] = useState<ShiftHubOption[]>([]);
  const [hubIds, setHubIds] = useState<number[]>([]);
  const [approved, setApproved] = useState(false);
  const [rows, setRows] = useState<YardReviewRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    shiftManagementService
      .getHubs()
      .then(setHubs)
      .catch(() => toast.error("Could not load yards."));
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await yardService.getReviews(hubIds, approved);
      setRows(result.rows ?? []);
      setHasSearched(true);
    } catch {
      toast.error("Could not load reviews.");
    } finally {
      setIsLoading(false);
    }
  }, [hubIds, approved]);

  const runApprove = async (row: YardReviewRow) => {
    setApprovingId(row.yardDailyId);
    try {
      const result = await yardService.approve(row.yardDailyId);
      if (result.success) {
        toast.success(result.message || "Approved.");
        await load();
      } else {
        toast.error(result.message || "Could not approve the shift.");
      }
    } catch {
      toast.error("Could not approve the shift.");
    } finally {
      setApprovingId(null);
    }
  };

  const columns: GridColDef[] = [
    { field: "shiftName", headerName: "Shift", width: 170, sortable: true },
    { field: "location", headerName: "Location", width: 150, sortable: true },
    { field: "yardDailyId", headerName: "Yard Shift #", width: 110, sortable: true },
    { field: "controlNumber", headerName: "Control #", width: 150, sortable: true },
    {
      field: "date",
      headerName: "Date",
      width: 110,
      sortable: true,
      renderCell: (params) => moment(params.row.date).format("MM/DD/YYYY"),
    },
    {
      field: "timeStart",
      headerName: "Times",
      width: 120,
      sortable: false,
      renderCell: (params) => `${params.row.timeStart || "—"} – ${params.row.timeEnd || "—"}`,
    },
    {
      field: "minutes",
      headerName: "Minutes",
      width: 90,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (params.row.minutes == null ? "—" : Math.round(params.row.minutes)),
    },
    { field: "miles", headerName: "Miles", width: 80, align: "right", headerAlign: "right" },
    { field: "trips", headerName: "Trips", width: 75, align: "right", headerAlign: "right" },
    { field: "status", headerName: "Status", width: 100, sortable: true },
    {
      field: "approvedBy",
      headerName: "Approved By",
      width: 190,
      sortable: false,
      renderCell: (params) =>
        params.row.isApproved
          ? `${params.row.approvedBy || "—"}${
              params.row.approvedDateTime ? ` · ${moment(params.row.approvedDateTime).format("MM/DD/YY HH:mm")}` : ""
            }`
          : "",
    },
    // Approving is Admin-only. The column is dropped rather than disabled: a greyed-out
    // Approve on every pending row reads as a broken screen, and a manager reviewing their
    // yard has no action to take here at all.
    ...(canApproveShift
      ? [
          {
            field: "actions",
            headerName: "",
            width: 120,
            sortable: false,
            renderCell: (params) => {
              const row = params.row as YardReviewRow;
              if (row.isApproved) return null;
              return (
                <Button
                  size="small"
                  variant="contained"
                  sx={SAVE_BUTTON_SX}
                  disabled={approvingId === row.yardDailyId}
                  onClick={(e) => {
                    e.stopPropagation();
                    runApprove(row);
                  }}
                >
                  {approvingId === row.yardDailyId ? "…" : "Approve"}
                </Button>
              );
            },
          } as GridColDef,
        ]
      : []),
  ];

  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Yard Review
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Approving a shift closes it and makes it billable. Minutes, miles and trips come from GPS processing and
        cannot be edited here.
        {!canApproveShift && " Approving is restricted to administrators, so this screen is view-only for you."}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="review-hubs-label">Yards</InputLabel>
            <Select
              labelId="review-hubs-label"
              multiple
              value={hubIds}
              onChange={(e) => setHubIds(e.target.value as number[])}
              input={<OutlinedInput label="Yards" />}
              renderValue={(selected) =>
                selected.length === 0
                  ? "All yards"
                  : hubs
                      .filter((h) => (selected as number[]).includes(h.hubId))
                      .map((h) => h.location)
                      .join(", ")
              }
            >
              {hubs.map((hub) => (
                <MenuItem key={hub.hubId} value={hub.hubId}>
                  <Checkbox checked={hubIds.includes(hub.hubId)} size="small" />
                  <ListItemText primary={hub.location} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="review-state-label">Show</InputLabel>
            <Select
              labelId="review-state-label"
              label="Show"
              value={approved ? "Approved" : "Pending"}
              onChange={(e) => setApproved(e.target.value === "Approved")}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" color="info" onClick={load} disabled={isLoading}>
            Get Reviews
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ height: "calc(100vh - 330px)", minHeight: 380 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.yardDailyId}
          loading={isLoading}
          density="compact"
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
          sx={{ fontSize: ".85em" }}
          onRowClick={(params) => navigate(`/shifts/logistrics/daily/${params.row.yardDailyId}`)}
          slots={{
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {hasSearched
                  ? `No ${approved ? "approved" : "pending"} shifts for this selection.`
                  : "Choose yards, then Get Reviews."}
              </Stack>
            ),
          }}
        />
      </Paper>
    </Box>
  );
};

export default YardReview;
